import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import axios from "axios";
import { updateUserAdditionalUrl, userAddUrl } from "api/endpoints";
import { getStoredSession, hydrateStoredSession } from "utils/userIdentity";

const userColumns = ["Name", "Surname", "Email", "Number", "Username", "Role", "Status"];
const roleByUserTypeId = {
  1: "Admin",
  2: "User",
  3: "Admin",
};
const roleTypeIdByLabel = {
  Admin: 1,
  User: 2,
};
const emptyUserForm = {
  name: "",
  surname: "",
  email: "",
  password: "",
  contactNumber: "",
  username: "",
  role: "User",
  registered: false,
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getSessionUserId = (session) =>
  pickFirst(session?.id, session?.ID, session?.userID, session?.userId, session?.UserID);

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return Boolean(value);
};

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  return payload ? [payload] : [];
};

function AdministrationUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionUserId, setSessionUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyUserForm);
  const [saving, setSaving] = useState(false);
  const [instructionUniqueID, setInstructionUniqueID] = useState("");
  const passwordResetUrl = instructionUniqueID
    ? `https://dash.omniofarm.com/pwdreset/${instructionUniqueID}`
    : "";

  const loadUsers = useCallback(async (active = { current: true }) => {
    setLoading(true);
    setError("");

    try {
      const session = getStoredSession() ?? (await hydrateStoredSession());
      const userId = getSessionUserId(session);

      if (!userId) {
        if (active.current) {
          setUsers([]);
          setSessionUserId(null);
          setError("No logged-in user ID was found.");
        }
        return;
      }

      setSessionUserId(userId);
      const response = await axios.get(userAddUrl(userId));
      if (active.current) {
        setUsers(normalizeRows(response.data));
      }
    } catch (loadError) {
      console.error("Unable to load administration users", loadError);
      if (active.current) {
        setUsers([]);
        setError("Unable to load users.");
      }
    } finally {
      if (active.current) {
        setLoading(false);
      }
    }
  }, []);

  const openAddDialog = () => {
    setEditingUser(null);
    setForm(emptyUserForm);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    const userTypeId = Number(pickFirst(user?.userTypeID, user?.userTypeId, user?.UserTypeID));
    setEditingUser(user);
    setForm({
      name: pickFirst(user?.name, user?.Name) || "",
      surname: pickFirst(user?.surname, user?.Surname) || "",
      email: pickFirst(user?.email, user?.Email) || "",
      password: pickFirst(user?.password, user?.Password) || "",
      contactNumber: pickFirst(user?.contactNumber, user?.ContactNumber, user?.number) || "",
      username: pickFirst(user?.username, user?.userName, user?.Username) || "",
      role: userTypeId === 1 || userTypeId === 3 ? "Admin" : "User",
      registered: toBoolean(
        pickFirst(user?.registered, user?.Registered, user?.isRegistered, false)
      ),
    });
    setDialogOpen(true);
  };

  const closeDialog = (force = false) => {
    if (saving && !force) return;
    setDialogOpen(false);
    setEditingUser(null);
    setForm(emptyUserForm);
  };

  const updateField = (field) => (event) => {
    const value = field === "registered" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveUser = async () => {
    const currentUserId = sessionUserId ?? getSessionUserId(getStoredSession());
    if (!currentUserId) {
      setError("No logged-in user ID was found.");
      return;
    }

    const editingId = pickFirst(editingUser?.id, editingUser?.ID);
    const payload = {
      name: form.name,
      surname: form.surname,
      email: form.email,
      password: form.password,
      contactNumber: form.contactNumber,
      registered: form.registered,
      userTypeID: roleTypeIdByLabel[form.role],
      username: form.username,
      userID: currentUserId,
    };

    if (editingId != null) {
      payload.id = editingId;
    }

    setSaving(true);
    try {
      const response = await axios.post(updateUserAdditionalUrl(), payload);
      await loadUsers();
      closeDialog(true);
      setInstructionUniqueID(response.data?.uniqueID || "");
    } catch (saveError) {
      console.error("Unable to save administration user", saveError);
      setError("Unable to save user.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const isActive = { current: true };

    loadUsers(isActive);

    return () => {
      isActive.current = false;
    };
  }, [loadUsers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <MDBox px={1} mb={3}>
          <MDTypography variant="h4" fontWeight="medium">
            Users
          </MDTypography>
        </MDBox>
        <TableContainer
          component={Box}
          sx={{
            boxShadow: "none",
            width: "fit-content",
            maxWidth: "100%",
            mx: "auto",
            overflowX: "auto",
          }}
        >
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "26cm",
              minWidth: "26cm",
              borderCollapse: "collapse",
              "& th, & td": {
                border: "1px solid",
                borderColor: "grey.300",
                fontSize: "0.9rem",
                lineHeight: 1.2,
                p: 0.5,
                textAlign: "center",
                color: "text.primary",
              },
            }}
          >
            <colgroup>
              <col style={{ width: "3.4cm" }} />
              <col style={{ width: "3.4cm" }} />
              <col style={{ width: "5.4cm" }} />
              <col style={{ width: "3.4cm" }} />
              <col style={{ width: "4cm" }} />
              <col style={{ width: "2cm" }} />
              <col style={{ width: "2cm" }} />
              <col style={{ width: "2.4cm" }} />
            </colgroup>
            <TableBody>
              <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                {userColumns.map((column) => (
                  <TableCell key={column} sx={{ fontWeight: 700 }}>
                    {column}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700 }}>
                  <IconButton
                    size="small"
                    aria-label="add user"
                    title="Add user"
                    onClick={openAddDialog}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "grey.800",
                      "&:hover": { bgcolor: "grey.200" },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 2, color: "text.secondary" }}>
                    <CircularProgress size={18} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 2, color: "error.main" }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 2, color: "text.secondary" }}>
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => {
                  const userTypeId = Number(
                    pickFirst(user?.userTypeID, user?.userTypeId, user?.UserTypeID)
                  );
                  const registered = toBoolean(
                    pickFirst(user?.registered, user?.Registered, user?.isRegistered, false)
                  );
                  const rowKey = pickFirst(user?.id, user?.ID, user?.uniqueID, user?.userID, index);

                  return (
                    <TableRow key={rowKey} sx={{ height: "0.64cm" }}>
                      <TableCell>{pickFirst(user?.name, user?.Name) || ""}</TableCell>
                      <TableCell>{pickFirst(user?.surname, user?.Surname) || ""}</TableCell>
                      <TableCell>{pickFirst(user?.email, user?.Email) || ""}</TableCell>
                      <TableCell>
                        {pickFirst(user?.contactNumber, user?.ContactNumber, user?.number) || ""}
                      </TableCell>
                      <TableCell>
                        {pickFirst(user?.username, user?.userName, user?.Username) || ""}
                      </TableCell>
                      <TableCell>{roleByUserTypeId[userTypeId] || ""}</TableCell>
                      <TableCell>
                        <Checkbox checked={registered} disabled size="small" />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            gap: 1,
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <EditIcon
                            onClick={() => openEditDialog(user)}
                            sx={{ fontSize: 30, color: "text.secondary", cursor: "pointer" }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1.5 }}>{editingUser ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={form.name}
                onChange={updateField("name")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Surname"
                fullWidth
                size="small"
                value={form.surname}
                onChange={updateField("surname")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                fullWidth
                size="small"
                value={form.email}
                onChange={updateField("email")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Number"
                fullWidth
                size="small"
                value={form.contactNumber}
                onChange={updateField("contactNumber")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={form.username}
                onChange={updateField("username")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                fullWidth
                size="small"
                type="password"
                value={form.password}
                onChange={updateField("password")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Role"
                fullWidth
                size="small"
                value={form.role}
                onChange={updateField("role")}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "1cm",
                  },
                  "& .MuiSelect-select": {
                    minHeight: "1cm",
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                  },
                }}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} />
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.registered}
                      onChange={updateField("registered")}
                      size="small"
                    />
                  }
                  label="Activate"
                />
                <MDBox display="flex" justifyContent="flex-end" gap={1}>
                  <Button variant="text" color="dark" onClick={closeDialog} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveUser}
                    disabled={saving}
                    sx={{ color: "#fff" }}
                  >
                    {editingUser ? "Save" : "Add"}
                  </Button>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(instructionUniqueID)}
        onClose={() => setInstructionUniqueID("")}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>User Saved</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            <Box component="span" sx={{ fontWeight: 500 }}>
              Please provide the user the link below to reset their password:
            </Box>
            <br />
            {passwordResetUrl}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setInstructionUniqueID("")}
            sx={{ color: "#fff" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default AdministrationUsers;
